import { useEffect, useRef, useState } from "react";
import { db } from "../lib/api";
import { useDebounced, useQuery } from "../lib/hooks";
import { openPerson } from "./PersonDrawer";
import { IconSearch } from "./icons";

type Hit = { id: string; full_name: string; known_as: string | null; email: string | null; phone: string | null; is_client: boolean };

/** One search across everyone (leads, clients, team), whatever list they sit in. "/" focuses it. */
export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const dq = useDebounced(q.trim(), 200);
  const box = useRef<HTMLInputElement>(null);
  const digits = dq.replace(/\D/g, "");
  const safe = dq.replace(/[*,()]/g, " ");
  const res = useQuery(dq.length >= 2 ? `gsearch:${dq}` : null, () => db.select<Hit>("people", {
    select: "id,full_name,known_as,email,phone,is_client", deleted_at: "is.null", limit: 12, order: "full_name",
    or: `(full_name.ilike.*${safe}*,known_as.ilike.*${safe}*,email.ilike.*${safe}*${digits.length >= 4 ? `,phone_digits.like.*${digits}*` : ""})`,
  }));
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "/" && !/input|textarea|select/i.test(t.tagName) && !t.isContentEditable) { e.preventDefault(); box.current?.focus(); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
  const hits = res.data ?? [];
  const pick = (h: Hit) => { openPerson(h.id); setQ(""); setOpen(false); box.current?.blur(); };
  return (
    <div className="gsearch">
      <IconSearch size={15} />
      <input ref={box} value={q} placeholder="Search anyone  ( / )" aria-label="Search everyone"
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, hits.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
          if (e.key === "Enter" && hits[hi]) pick(hits[hi]);
          if (e.key === "Escape") { setQ(""); box.current?.blur(); }
        }} />
      {open && dq.length >= 2 && (
        <div className="gsearch-pop" role="listbox">
          {hits.map((h, i) => (
            <button key={h.id} role="option" aria-selected={i === hi} className={i === hi ? "on" : ""} onMouseDown={(e) => { e.preventDefault(); pick(h); }}>
              <span>{h.full_name}{h.known_as && <span className="known-as"> · {h.known_as}</span>}{h.is_client && <span className="chip" style={{ marginLeft: 6 }}>Client</span>}</span>
              <span className="small muted">{h.email ?? h.phone ?? ""}</span>
            </button>
          ))}
          {res.data && hits.length === 0 && <div className="small muted" style={{ padding: 10 }}>No one matches “{dq}”.</div>}
        </div>
      )}
    </div>
  );
}
